import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import theme from '../theme';

function toISODate(d) {
  return d.toISOString().split('T')[0];
}

function getPresetRange(preset) {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();

  switch (preset) {
    case 'mese_corrente':
      return { da: new Date(y, m, 1), a: today };
    case 'mese_scorso': {
      const da = new Date(y, m - 1, 1);
      const a = new Date(y, m, 0);
      return { da, a };
    }
    case 'mese_prima_scorso': {
      const da = new Date(y, m - 2, 1);
      const a = new Date(y, m - 1, 0);
      return { da, a };
    }
    case 'ultimi_3_mesi':
      return { da: new Date(y, m - 3, 1), a: today };
    case 'ultimi_6_mesi':
      return { da: new Date(y, m - 6, 1), a: today };
    case 'inizio_anno':
      return { da: new Date(y, 0, 1), a: today };
    case 'ultimo_anno':
      return { da: new Date(y - 1, m, today.getDate()), a: today };
    default:
      return { da: new Date(y, m, 1), a: today };
  }
}

function presetLabel(preset) {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth();
  const capitalize = s => s.charAt(0).toUpperCase() + s.slice(1);

  switch (preset) {
    case 'mese_corrente':
      return 'Mese in corso';
    case 'mese_scorso': {
      const d = new Date(y, m - 1, 1);
      return capitalize(d.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' }));
    }
    case 'mese_prima_scorso': {
      const d = new Date(y, m - 2, 1);
      return capitalize(d.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' }));
    }
    case 'ultimi_3_mesi': return 'Ultimi 3 mesi';
    case 'ultimi_6_mesi': return 'Ultimi 6 mesi';
    case 'inizio_anno': return 'Da inizio anno';
    case 'ultimo_anno': return 'Ultimo anno';
    case 'personalizzato': return 'Personalizzato';
    default: return preset;
  }
}

const PRESETS = ['mese_corrente', 'mese_scorso', 'mese_prima_scorso', 'ultimi_3_mesi', 'ultimi_6_mesi', 'inizio_anno', 'ultimo_anno', 'personalizzato'];

const FUNNEL_STEP_LABELS = {
  annuncio_visualizzato: 'Annuncio visto',
  candidatura_inviata: 'Candidatura inviata',
  chat_visualizzata: 'Chat visualizzata',
  offerta_inviata: 'Offerta inviata',
  offerta_accettata: 'Offerta accettata',
};

const TREND_METRICS = [
  { key: 'nuove_registrazioni', label: 'Nuove registrazioni' },
  { key: 'annunci_pubblicati', label: 'Annunci pubblicati' },
  { key: 'candidature_inviate', label: 'Candidature inviate' },
  { key: 'gmv', label: 'GMV (€)' },
  { key: 'commissioni', label: 'Commissioni (€)' },
];

function round1(n) {
  return Math.round(n * 10) / 10;
}

function DateRangePicker({ preset, setPreset, customDa, setCustomDa, customA, setCustomA }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: preset === 'personalizzato' ? 10 : 0 }}>
        {PRESETS.map(p => (
          <button key={p} onClick={() => setPreset(p)} style={{
            padding: '6px 12px', borderRadius: theme.radius.pill,
            border: `1px solid ${preset === p ? theme.colors.primary : theme.colors.border}`,
            background: preset === p ? theme.colors.primary : theme.colors.white,
            color: preset === p ? theme.colors.white : theme.colors.textSecondary,
            fontSize: theme.font.size.xs, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap'
          }}>
            {presetLabel(p)}
          </button>
        ))}
      </div>
      {preset === 'personalizzato' && (
        <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted }}>
            Da:
            <input type="date" value={customDa} onChange={e => setCustomDa(e.target.value)}
              style={{ marginLeft: 6, padding: '6px 8px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}` }} />
          </label>
          <label style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted }}>
            A:
            <input type="date" value={customA} onChange={e => setCustomA(e.target.value)}
              style={{ marginLeft: 6, padding: '6px 8px', borderRadius: theme.radius.sm, border: `1px solid ${theme.colors.borderInput}` }} />
          </label>
        </div>
      )}
    </div>
  );
}

function MetricCard({ label, value, sublabel }) {
  return (
    <div style={{
      background: theme.colors.white, border: `1px solid ${theme.colors.border}`,
      borderRadius: theme.radius.lg, padding: theme.spacing.lg, flex: '1 1 140px', minWidth: 140
    }}>
      <div style={{ fontSize: theme.font.size.xs, color: theme.colors.textMuted, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: theme.font.size.xxl, fontWeight: 700, color: theme.colors.primary }}>{value}</div>
      {sublabel && <div style={{ fontSize: theme.font.size.xs, color: theme.colors.textMuted, marginTop: 2 }}>{sublabel}</div>}
    </div>
  );
}

function CollapsibleSection({ title, icon, defaultOpen = false, children, compact = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: compact ? 8 : 14, border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.lg, overflow: 'hidden', background: theme.colors.white }}>
      <button onClick={() => setOpen(o => !o)} style={{
        width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: compact ? '10px 14px' : '14px 16px', border: 'none', background: theme.colors.backgroundAlt,
        cursor: 'pointer', fontSize: compact ? theme.font.size.base : theme.font.size.md, fontWeight: 700, color: theme.colors.textPrimary
      }}>
        <span>{icon} {title}</span>
        <span style={{ fontSize: theme.font.size.lg, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }}>▾</span>
      </button>
      {open && (
        <div style={{ padding: compact ? theme.spacing.md : theme.spacing.lg }}>
          {children}
        </div>
      )}
    </div>
  );
}

function FunnelSteps({ steps }) {
  if (!steps || steps.length === 0) return null;
  return (
    <div style={{ background: theme.colors.backgroundAlt, borderRadius: theme.radius.md, overflow: 'hidden', marginTop: 12 }}>
      {steps.map((s, i) => {
        const prev = i > 0 ? steps[i - 1].count : null;
        const dropPct = prev && prev > 0 ? round1(100 * (1 - s.count / prev)) : null;
        return (
          <div key={s.step} style={{ padding: '10px 12px', borderBottom: i < steps.length - 1 ? `1px solid ${theme.colors.border}` : 'none' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: theme.font.size.base, fontWeight: 600 }}>
              <span>{FUNNEL_STEP_LABELS[s.step] || s.step}</span>
              <span style={{ color: theme.colors.primary }}>{s.count}</span>
            </div>
            {dropPct !== null && (
              <div style={{ fontSize: theme.font.size.xs, color: dropPct > 0 ? theme.colors.errorDark : theme.colors.textMuted, marginTop: 2 }}>
                {dropPct > 0 ? `▼ ${dropPct}% abbandono rispetto allo step precedente` : 'Nessun calo rispetto allo step precedente'}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ProvinceSection({ data, defaultOpen }) {
  return (
    <CollapsibleSection
      compact
      defaultOpen={defaultOpen}
      title={`${data.provincia} — ${data.pct_registrazioni ?? 0}% delle registrazioni`}
      icon="📍"
    >
      <div style={{ fontSize: theme.font.size.sm, fontWeight: 600, color: theme.colors.textSecondary, marginBottom: 6 }}>Acquisizione</div>
      <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap', marginBottom: 12 }}>
        <MetricCard label="Registrazioni (periodo)" value={data.registrazioni_periodo ?? 0} sublabel={`${data.pct_registrazioni ?? 0}% del nazionale`} />
        <MetricCard label="Totale utenti" value={data.totale_utenti ?? 0} sublabel={`${data.pct_totale_utenti ?? 0}% del nazionale`} />
        <MetricCard label="Utenti A" value={data.utenti_a ?? 0} sublabel={`${data.pct_utenti_a ?? 0}% del tot. A naz.`} />
        <MetricCard label="Utenti B" value={data.utenti_b ?? 0} sublabel={`${data.pct_utenti_b ?? 0}% del tot. B naz.`} />
        <MetricCard label="Utenti C" value={data.utenti_c ?? 0} sublabel={`${data.pct_utenti_c ?? 0}% del tot. C naz.`} />
        <MetricCard label="Nessuna categoria" value={data.utenti_nessuna_categoria ?? 0} sublabel={`${data.pct_utenti_nessuna_categoria ?? 0}% del naz.`} />
      </div>

      <div style={{ fontSize: theme.font.size.sm, fontWeight: 600, color: theme.colors.textSecondary, marginBottom: 6 }}>Transazione</div>
      <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap', marginBottom: 12 }}>
        <MetricCard label="GMV lato committente" value={`€${data.gmv_committente ?? 0}`} sublabel={`${data.pct_gmv_committente ?? 0}% del GMV naz.`} />
        <MetricCard label="Commissioni" value={`€${data.commissione_committente ?? 0}`} sublabel={`${data.pct_commissione_committente ?? 0}% delle commissioni naz.`} />
        <MetricCard label="Guadagni lato lavoratore" value={`€${data.guadagni_lavoratore ?? 0}`} sublabel={`${data.pct_guadagni_lavoratore ?? 0}% dei guadagni naz.`} />
        <MetricCard label="Ticket medio (committente)" value={`€${data.ticket_medio_committente ?? 0}`} />
      </div>

      <div style={{ fontSize: theme.font.size.sm, fontWeight: 600, color: theme.colors.textSecondary, marginBottom: 6 }}>Supply Health</div>
      <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap', marginBottom: 12 }}>
        <MetricCard label="Annunci pubblicati" value={data.annunci_pubblicati ?? 0} sublabel={`${data.pct_annunci ?? 0}% del naz.`} />
        <MetricCard label="Media candidature/annuncio" value={data.media_candidature_annuncio ?? 0} />
        <MetricCard label="Lavoratori onboarded" value={data.lavoratori_onboarded ?? 0} sublabel={`${data.pct_lavoratori_onboarded ?? 0}% del naz.`} />
      </div>

      {data.per_categoria?.length > 0 && (
        <>
          <div style={{ fontSize: theme.font.size.sm, fontWeight: 600, color: theme.colors.textSecondary, margin: '10px 0 6px' }}>
            Per categoria (ordinato per GMV)
          </div>
          <div style={{ background: theme.colors.backgroundAlt, borderRadius: theme.radius.md, overflow: 'hidden' }}>
            {data.per_categoria.map((c, i) => (
              <div key={i} style={{ padding: '8px 12px', borderBottom: i < data.per_categoria.length - 1 ? `1px solid ${theme.colors.border}` : 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: theme.font.size.sm, fontWeight: 600 }}>
                  <span>{c.category || '—'}</span>
                  <span style={{ color: theme.colors.primary }}>€{c.gmv} GMV</span>
                </div>
                <div style={{ fontSize: theme.font.size.xs, color: theme.colors.textMuted, marginTop: 2 }}>
                  {c.annunci} annunci · {c.media_candidature_annuncio} candid./annuncio · {c.lavori_pagati} pagati · €{c.ticket_medio} ticket medio
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </CollapsibleSection>
  );
}

function CohortTable({ coorti }) {
  if (!coorti || coorti.length === 0) {
    return <p style={{ textAlign: 'center', color: theme.colors.textMuted, fontSize: theme.font.size.sm }}>Nessuna coorte disponibile.</p>;
  }
  const checkpointMesi = [1, 2, 3, 6, 9, 12, 18, 24];

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: theme.font.size.xs }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: `2px solid ${theme.colors.border}`, whiteSpace: 'nowrap' }}>Coorte</th>
            <th style={{ textAlign: 'right', padding: '6px 8px', borderBottom: `2px solid ${theme.colors.border}`, whiteSpace: 'nowrap' }}>N°</th>
            {checkpointMesi.map(m => (
              <th key={m} style={{ textAlign: 'right', padding: '6px 8px', borderBottom: `2px solid ${theme.colors.border}`, whiteSpace: 'nowrap' }}>{m}m</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {coorti.map((c, i) => (
            <tr key={i}>
              <td style={{ padding: '6px 8px', borderBottom: `1px solid ${theme.colors.border}`, whiteSpace: 'nowrap', fontWeight: 600 }}>{c.coorte}</td>
              <td style={{ padding: '6px 8px', borderBottom: `1px solid ${theme.colors.border}`, textAlign: 'right', color: theme.colors.textMuted }}>{c.dimensione}</td>
              {checkpointMesi.map(m => {
                const cp = c.checkpoint?.find(x => x.mesi === m);
                return (
                  <td key={m} style={{ padding: '6px 8px', borderBottom: `1px solid ${theme.colors.border}`, textAlign: 'right' }}>
                    {!cp || !cp.raggiunto ? <span style={{ color: '#ccc' }}>—</span> : (
                      <span style={{ color: cp.pct >= 30 ? theme.colors.primary : theme.colors.textMuted, fontWeight: cp.pct >= 30 ? 700 : 400 }}>
                        {cp.pct}%
                      </span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ fontSize: theme.font.size.xs, color: theme.colors.textMuted, marginTop: 8 }}>
        "—" indica un checkpoint non ancora raggiunto (troppo tempo dal presente). Percentuale: quota della coorte con almeno un'attività proprio in quel mese specifico.
      </div>
    </div>
  );
}

function WeeklyTrendChart({ trend }) {
  const [metric, setMetric] = useState('gmv');
  if (!trend || trend.length === 0) {
    return <p style={{ textAlign: 'center', color: theme.colors.textMuted, fontSize: theme.font.size.sm }}>Nessun dato disponibile per il periodo selezionato.</p>;
  }
  const activeLabel = TREND_METRICS.find(m => m.key === metric)?.label || metric;

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {TREND_METRICS.map(m => (
          <button key={m.key} onClick={() => setMetric(m.key)} style={{
            padding: '5px 10px', borderRadius: theme.radius.pill,
            border: `1px solid ${metric === m.key ? theme.colors.primary : theme.colors.border}`,
            background: metric === m.key ? theme.colors.primary : theme.colors.white,
            color: metric === m.key ? theme.colors.white : theme.colors.textSecondary,
            fontSize: theme.font.size.xs, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap'
          }}>
            {m.label}
          </button>
        ))}
      </div>
      <div style={{ width: '100%', height: 220 }}>
        <ResponsiveContainer>
          <LineChart data={trend} margin={{ top: 8, right: 8, bottom: 0, left: -20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
            <XAxis dataKey="settimana" tick={{ fontSize: 10 }} interval={Math.ceil(trend.length / 8)} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip contentStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey={metric} name={activeLabel} stroke={theme.colors.primary} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function AdminMetrics() {
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState(null);

  const [preset, setPreset] = useState('mese_corrente');
  const [customDa, setCustomDa] = useState(toISODate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)));
  const [customA, setCustomA] = useState(toISODate(new Date()));

  useEffect(() => { fetchMetrics(); }, [preset, customDa, customA]);

  const fetchMetrics = async () => {
    setMetricsLoading(true);
    setMetricsError(null);

    let dataDa, dataA;
    if (preset === 'personalizzato') {
      dataDa = customDa;
      dataA = customA;
    } else {
      const range = getPresetRange(preset);
      dataDa = toISODate(range.da);
      dataA = toISODate(range.a);
    }

    const { data, error } = await supabase.rpc('get_admin_metrics', { p_data_da: dataDa, p_data_a: dataA });
    if (error) {
      setMetricsError(error.message);
    } else {
      setMetrics(data);
    }
    setMetricsLoading(false);
  };

  return (
    <>
      <DateRangePicker
        preset={preset} setPreset={setPreset}
        customDa={customDa} setCustomDa={setCustomDa}
        customA={customA} setCustomA={setCustomA}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontSize: theme.font.size.sm, color: theme.colors.textMuted }}>
          {metrics?.periodo ? `Dal ${metrics.periodo.da} al ${metrics.periodo.a}` : 'Dati in tempo reale da Supabase'}
        </div>
        <button onClick={fetchMetrics} disabled={metricsLoading} style={{
          background: 'none', border: `1px solid ${theme.colors.primary}`, color: theme.colors.primary,
          borderRadius: theme.radius.sm, padding: '6px 14px', fontSize: theme.font.size.sm,
          fontWeight: 600, cursor: metricsLoading ? 'not-allowed' : 'pointer'
        }}>
          {metricsLoading ? '⏳ Aggiorno...' : '🔄 Aggiorna'}
        </button>
      </div>

      {metricsError && (
        <div style={{ background: theme.colors.errorBg, color: theme.colors.errorDark, padding: 12, borderRadius: theme.radius.sm, fontSize: theme.font.size.sm, marginBottom: 14 }}>
          Errore nel caricamento: {metricsError}
        </div>
      )}

      {metricsLoading && !metrics && (
        <p style={{ textAlign: 'center', color: theme.colors.textMuted, marginTop: 30 }}>Caricamento metriche...</p>
      )}

      {metrics && (
        <>
          <CollapsibleSection title="Trend settimanale" icon="📊" defaultOpen={true}>
            <div style={{ fontSize: theme.font.size.xs, color: theme.colors.textMuted, marginBottom: 10 }}>
              Segue il filtro data selezionato sopra, con granularità settimanale.
            </div>
            <WeeklyTrendChart trend={metrics.trend_settimanale} />
          </CollapsibleSection>

          <CollapsibleSection title="Acquisizione" icon="👥">
            <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap', marginBottom: 12 }}>
              <MetricCard label="Nuove registrazioni (periodo)" value={metrics.acquisizione?.nuove_registrazioni_periodo ?? 0} />
              <MetricCard label="Totale registrazioni" value={metrics.acquisizione?.totale_registrazioni ?? 0} sublabel="da sempre" />
            </div>

            <div style={{ fontSize: theme.font.size.sm, fontWeight: 600, color: theme.colors.textSecondary, margin: '10px 0 6px' }}>
              Composizione base utenti (periodo)
            </div>
            <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap' }}>
              <MetricCard label="Utenti A" value={metrics.composizione_utenti?.utenti_a ?? 0}
                sublabel={`${metrics.composizione_utenti?.pct_a ?? 0}% · committenti senza onboarding`} />
              <MetricCard label="Utenti B" value={metrics.composizione_utenti?.utenti_b ?? 0}
                sublabel={`${metrics.composizione_utenti?.pct_b ?? 0}% · solo lavoratori`} />
              <MetricCard label="Utenti C" value={metrics.composizione_utenti?.utenti_c ?? 0}
                sublabel={`${metrics.composizione_utenti?.pct_c ?? 0}% · entrambi i ruoli`} />
              <MetricCard label="Nessuna categoria" value={metrics.composizione_utenti?.utenti_nessuna_categoria ?? 0} sublabel="registrati, non ancora attivi" />
            </div>
            <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap', marginTop: 8 }}>
              <MetricCard label="Rapporto A:B"
                value={metrics.composizione_utenti?.rapporto_a_b != null ? `${metrics.composizione_utenti.rapporto_a_b} : 1` : '—'} />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Transazione" icon="💰">
            <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap', marginBottom: 12 }}>
              <MetricCard label="GMV totale" value={`€${metrics.transazione?.gmv_totale ?? 0}`} />
              <MetricCard label="Ticket medio" value={`€${metrics.transazione?.ticket_medio_generale ?? 0}`} />
              <MetricCard label="Ricavo commissioni" value={`€${metrics.transazione?.ricavo_commissioni_totale ?? 0}`} />
            </div>
            {metrics.transazione?.per_categoria?.length > 0 && (
              <>
                <div style={{ fontSize: theme.font.size.sm, fontWeight: 600, color: theme.colors.textSecondary, margin: '10px 0 6px' }}>
                  Per categoria (ordinato per GMV)
                </div>
                <div style={{ background: theme.colors.backgroundAlt, borderRadius: theme.radius.md, overflow: 'hidden' }}>
                  {metrics.transazione.per_categoria.map((c, i) => (
                    <div key={i} style={{ padding: '10px 12px', borderBottom: i < metrics.transazione.per_categoria.length - 1 ? `1px solid ${theme.colors.border}` : 'none' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: theme.font.size.base, fontWeight: 600 }}>
                        <span>{c.category || '—'}</span>
                        <span style={{ color: theme.colors.primary }}>€{c.gmv} GMV</span>
                      </div>
                      <div style={{ fontSize: theme.font.size.xs, color: theme.colors.textMuted, marginTop: 2 }}>
                        €{c.ticket_medio} ticket medio · {c.count} lavori pagati
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CollapsibleSection>

          <CollapsibleSection title="Supply Health" icon="🧑‍🔧">
            <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap', marginBottom: 12 }}>
              <MetricCard label="Annunci pubblicati" value={metrics.supply?.annunci_pubblicati_totali ?? 0} />
              <MetricCard label="Media candidature/annuncio" value={metrics.supply?.media_candidature_per_annuncio ?? 0} />
            </div>
            {metrics.supply?.per_categoria?.length > 0 && (
              <div style={{ background: theme.colors.backgroundAlt, borderRadius: theme.radius.md, overflow: 'hidden' }}>
                {metrics.supply.per_categoria.map((c, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', borderBottom: i < metrics.supply.per_categoria.length - 1 ? `1px solid ${theme.colors.border}` : 'none', fontSize: theme.font.size.sm }}>
                    <span>{c.category || '—'}</span>
                    <span style={{ color: theme.colors.textMuted }}>{c.annunci} annunci · {c.media_candidature} candid./annuncio</span>
                  </div>
                ))}
              </div>
            )}
          </CollapsibleSection>

          <CollapsibleSection title="Retention" icon="📈">
            <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap' }}>
              <MetricCard label="Utenti attivi (periodo)" value={metrics.retention?.utenti_attivi_periodo ?? 0} />
              <MetricCard label="Eventi/utente (periodo)" value={metrics.retention?.eventi_per_utente_periodo ?? 0} />
              <MetricCard label="Coppie ricorrenti" value={metrics.retention?.coppie_ricorrenti ?? 0} sublabel="stima per nome autore" />
              <MetricCard label="% seconda transazione" value={`${metrics.retention?.pct_seconda_transazione ?? 0}%`} sublabel="sul totale match" />
            </div>

            <div style={{ fontSize: theme.font.size.sm, fontWeight: 600, color: theme.colors.textSecondary, margin: '16px 0 6px' }}>
              Retention per coorte
            </div>
            <div style={{ fontSize: theme.font.size.xs, color: theme.colors.textMuted, marginBottom: 10 }}>
              Sempre calcolata su tutta la storia della piattaforma, indipendentemente dal filtro data sopra.
              Percentuale della coorte attiva proprio in quel mese specifico dopo la registrazione.
            </div>
            <CohortTable coorti={metrics.retention_coorti} />
          </CollapsibleSection>

          <CollapsibleSection title="Funnel / Matching" icon="🔄">
            <FunnelSteps steps={metrics.funnel_steps} />
            <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap', marginTop: 12 }}>
              <MetricCard label="Time-to-first-candidate" value={`${metrics.time_to_first_candidate_hours ?? '—'}h`} />
              <MetricCard label="Match completati" value={metrics.matching?.match_completati ?? 0} />
              <MetricCard label="Tasso annullamento" value={`${metrics.matching?.tasso_annullamento_pct ?? 0}%`} />
              <MetricCard label="Lavori completati" value={metrics.matching?.lavori_completati ?? 0} />
              <MetricCard label="Tasso recensione" value={`${metrics.matching?.tasso_recensione_pct ?? 0}%`} />
            </div>
            <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap', marginTop: 8 }}>
              <MetricCard label="Match-rate lato A" value={`${metrics.matching?.match_rate_a_pct ?? 0}%`} sublabel="annunci con ≥1 candidatura" />
              <MetricCard label="Match-rate lato B" value={`${metrics.matching?.match_rate_b_pct ?? 0}%`} sublabel="candidati con ≥1 accettazione" />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Cancellazioni" icon="🚫">
            <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap', marginBottom: 12 }}>
              <MetricCard label="Cancellazioni totali" value={metrics.matching?.cancellazioni_totali_periodo ?? 0} />
              <MetricCard label="Tasso cancellazione vs completati" value={`${metrics.matching?.tasso_cancellazione_completati_pct ?? 0}%`} />
            </div>
            <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap' }}>
              <MetricCard label="Libera (>48h)" value={metrics.cancellazioni?.cancellazione_libera ?? 0} />
              <MetricCard label="Da lavoratore" value={metrics.cancellazioni?.cancellazione_lavoratore ?? 0} />
              <MetricCard label="Tardiva, rifiutata" value={metrics.cancellazioni?.cancellazione_tardiva_lavoratore_rifiuta ?? 0} />
              <MetricCard label="Tardiva, accettata" value={metrics.cancellazioni?.cancellazione_tardiva_lavoratore_accetta ?? 0} />
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Dati geografici" icon="🗺️">
            {(!metrics.dati_geografici || metrics.dati_geografici.length === 0) && (
              <p style={{ textAlign: 'center', color: theme.colors.textMuted, fontSize: theme.font.size.sm }}>
                Nessuna provincia con dati disponibili (profili con provincia non specificata sono esclusi).
              </p>
            )}
            {metrics.dati_geografici?.map((prov, i) => (
              <ProvinceSection key={prov.provincia} data={prov} defaultOpen={i === 0} />
            ))}
          </CollapsibleSection>

          <div style={{ marginTop: 8, padding: 14, background: theme.colors.backgroundAlt, borderRadius: theme.radius.md, fontSize: theme.font.size.xs, color: theme.colors.textMuted, lineHeight: 1.5 }}>
            Non ancora disponibili: N° download per zona (dato di store, non passa da Supabase),
            metriche premium (funzionalità non ancora implementata).
            Alcune metriche (lavori completati, composizione A/B/C, dati geografici lato committente) usano il matching
            per nome autore o la data di creazione della candidatura come approssimazione, non essendoci colonne dedicate
            a provincia del committente o data di completamento/onboarding.
          </div>
        </>
      )}
    </>
  );
}

export default AdminMetrics;